import { ContactShadows, Environment } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { DISTRESS_SCRATCH_REPEAT_RATIO_INCHES } from 'element-domain'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import type { Camera, Mesh, MeshStandardMaterial, Object3D, Scene } from 'three'
import { NoToneMapping } from 'three'

import { useResourceUrl } from '@salsita-ai/platform-project-sdk/react'

import { configuratorInterface } from '../systems/configurator-interface'

/** Must match finalize / snapshot cameras so offscreen renders see lit tabletop + base meshes. */
export const SCENE_TABLETOP_LAYER = 1
export const SCENE_BASE_LAYER = 2

/**
 * Negative mipmap LOD bias used when sampling the normal map. Pushes the GPU
 * to pick a sharper mip level than the default trilinear/anisotropic selection,
 * so high-frequency features (e.g. plank grooves baked into the normal map)
 * stay visible further into the distance. Lower (more negative) = sharper +
 * more shimmer; -1.0 is the standard conservative default.
 */
const NORMAL_MAP_LOD_BIAS = -3.0

/**
 * Walks the scene every frame and attaches an `onBeforeCompile` hook to any
 * MeshStandard/PhysicalMaterial that has a normalMap, injecting a negative
 * LOD bias into the normal map texture lookup AND combining a bumpMap (distress
 * scratches) on top of the normal map when both are present.
 *
 * The default Three.js shader uses an elif chain (normalMap OR bumpMap), but
 * we replace it so that the bump perturbation is layered ON TOP of the
 * already-perturbed normal from the normal map.
 *
 * `useFrame` is used because materials are created asynchronously (texture
 * load, wood species change, etc.) and may not exist on first mount.
 */
function NormalMapLodBiasEnforcer() {
  const { scene } = useThree()
  useFrame(() => {
    scene.traverse(obj => {
      const mesh = obj as Mesh
      if (!mesh.isMesh) return
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      for (const mat of materials) {
        if (!mat) continue
        const standardMat = mat as MeshStandardMaterial
        if (!standardMat.normalMap && !standardMat.bumpMap) continue
        if (mat.userData['__normalMapLodBiased']) continue

        const prevOnBeforeCompile = mat.onBeforeCompile.bind(mat)
        const biasLiteral = NORMAL_MAP_LOD_BIAS.toFixed(2)
        const hasBump = !!standardMat.bumpMap
        const cacheKey = `normalMapLodBias:${biasLiteral}:bump:${hasBump}`

        const biasedNormalFragmentMaps = `
#ifdef USE_NORMALMAP_OBJECTSPACE
  normal = texture2D( normalMap, vNormalMapUv, ${biasLiteral} ).xyz * 2.0 - 1.0;
  #ifdef FLIP_SIDED
    normal = - normal;
  #endif
  #ifdef DOUBLE_SIDED
    normal = normal * faceDirection;
  #endif
  normal = normalize( normalMatrix * normal );
#elif defined( USE_NORMALMAP_TANGENTSPACE )
  vec3 mapN = texture2D( normalMap, vNormalMapUv, ${biasLiteral} ).xyz * 2.0 - 1.0;
  mapN.xy *= normalScale;
  normal = normalize( tbn * mapN );
#endif
#ifdef USE_BUMPMAP
  normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );
#endif
`
        mat.onBeforeCompile = (shader, renderer) => {
          prevOnBeforeCompile?.(shader, renderer)
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            biasedNormalFragmentMaps
          )
        }
        mat.customProgramCacheKey = () => cacheKey
        mat.userData['__normalMapLodBiased'] = true
        mat.needsUpdate = true
      }
    })
  })
  return null
}

/**
 * Fixes the bump map (distress scratch) UV repeat so that scratch marks always
 * appear at a consistent physical size regardless of the selected wood species.
 *
 * The SDK applies the same repeatX/repeatY to ALL maps in a material override,
 * including the bump map. This ties the scratch scale to the wood texture tile
 * size, which varies per species. This component overrides the bump map repeat
 * after-the-fact each frame to a fixed physical size
 * (DISTRESS_SCRATCH_REPEAT_RATIO_INCHES).
 *
 * Formula: bumpRepeat = colorMapRepeat * (woodTextureSize / DISTRESS_SCRATCH_REPEAT_RATIO_INCHES)
 * This makes the scratch appear at DISTRESS_SCRATCH_REPEAT_RATIO_INCHES per tile
 * for any mesh and any wood species.
 */
function DistressBumpRepeatFixer() {
  const { scene } = useThree()
  const $table = configuratorInterface.useRootEntityState()

  const woodTextureSize = $table.$woodTextureSize ?? DISTRESS_SCRATCH_REPEAT_RATIO_INCHES
  const bumpMultiplier = woodTextureSize / DISTRESS_SCRATCH_REPEAT_RATIO_INCHES

  useFrame(() => {
    scene.traverse(obj => {
      const mesh = obj as Mesh
      if (!mesh.isMesh) return
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      for (const mat of mats) {
        const sm = mat as MeshStandardMaterial
        if (!sm.bumpMap || !sm.map) continue
        const imgSrc: string =
          (sm.bumpMap.image as HTMLImageElement | null)?.currentSrc ??
          (sm.bumpMap.image as HTMLImageElement | null)?.src ??
          ''
        if (!imgSrc.includes('Scratches')) continue
        const targetX = sm.map.repeat.x * bumpMultiplier
        const targetY = sm.map.repeat.y * bumpMultiplier
        if (sm.bumpMap.repeat.x !== targetX || sm.bumpMap.repeat.y !== targetY) {
          sm.bumpMap.repeat.set(targetX, targetY)
          sm.needsUpdate = true
        }
      }
    })
  })

  return null
}

/**
 * Multi-pass renderer that gives each object group its own lighting environment.
 * Intercepts gl.render to split the main render into per-layer passes so
 * Three.js collects only the lights matching each layer.
 *
 * Tagging runs on every frame (no caching) so that child meshes replaced after
 * async texture loads are immediately assigned to the correct lighting layer.
 * The meshes and lights also retain layer 0 for SDK renderers that capture the
 * scene without going through this multi-pass renderer.
 */
function SelectiveLightingRenderer() {
  const { gl, scene, camera } = useThree()
  const ready = useRef(false)

  useLayoutEffect(() => {
    camera.layers.enable(SCENE_TABLETOP_LAYER)
    camera.layers.enable(SCENE_BASE_LAYER)
  }, [camera])

  useLayoutEffect(() => {
    const originalRender = gl.render.bind(gl)

    gl.render = function (renderScene: Scene, renderCamera: Camera) {
      if (renderScene === scene && renderCamera === camera) {
        const layeredModelObjects = new Set<Object3D>()

        // Re-tag every frame so children replaced after async texture loads
        // (cold network cache) are never stuck on layer 0 which has no lights.
        scene.traverse(obj => {
          // Keep custom-layer lights visible to the SDK's separate preset
          // thumbnail renderer, whose camera only sees the default layer.
          if (obj.layers.isEnabled(SCENE_TABLETOP_LAYER) || obj.layers.isEnabled(SCENE_BASE_LAYER)) {
            obj.layers.enable(0)
          }

          let layer: number | null = null
          if (
            obj.name.startsWith('TOP_') ||
            obj.name.startsWith('APRON_') ||
            obj.name.startsWith('LEAVE_') ||
            obj.name.startsWith('CROSS_BRACE')
          )
            layer = SCENE_TABLETOP_LAYER
          else if (obj.name.startsWith('SUPPORT_') || obj.name.startsWith('PLATFORM_')) layer = SCENE_BASE_LAYER
          if (layer !== null) {
            ready.current = true
            const targetLayer = layer
            obj.traverse(child => {
              child.layers.set(0)
              child.layers.enable(targetLayer)
              layeredModelObjects.add(child)
            })
          }
        })

        if (!ready.current) {
          originalRender(scene, camera)
          return
        }

        const savedAutoClear = gl.autoClear

        // Pass 1: tabletop objects + tabletop lights
        camera.layers.set(SCENE_TABLETOP_LAYER)
        gl.autoClear = true
        originalRender(scene, camera)

        // Pass 2: base objects + base lights (composited)
        const savedBackground = scene.background
        scene.background = null
        camera.layers.set(SCENE_BASE_LAYER)
        gl.autoClear = false
        originalRender(scene, camera)

        // Pass 3: layer-0 objects (ContactShadows). The table also remains on
        // layer 0 for the SDK's preset renderer, so hide it for this pass to
        // avoid drawing it a second time over the selectively lit passes.
        for (const object of layeredModelObjects) object.layers.disable(0)
        try {
          camera.layers.set(0)
          originalRender(scene, camera)
        } finally {
          for (const object of layeredModelObjects) object.layers.enable(0)
        }

        // Restore
        scene.background = savedBackground
        gl.autoClear = savedAutoClear
        camera.layers.set(0)
        camera.layers.enable(SCENE_TABLETOP_LAYER)
        camera.layers.enable(SCENE_BASE_LAYER)
      } else {
        // Non-main renders (ContactShadows, etc.) — see all objects
        renderCamera.layers.enable(SCENE_TABLETOP_LAYER)
        renderCamera.layers.enable(SCENE_BASE_LAYER)
        originalRender(renderScene, renderCamera)
      }
    }

    return () => {
      gl.render = originalRender
    }
  }, [gl, scene, camera])

  return null
}

export default configuratorInterface.createCustomPhoenixLayoutElement('SceneEnvironment', () => {
  const { gl } = useThree()
  const { getResourceUrl } = useResourceUrl()

  const $table = configuratorInterface.useRootEntityState()

  const environmentUrl = useMemo(() => {
    return getResourceUrl('environments/interior_haussmannian_front_day_light_07.hdr')
  }, [getResourceUrl])

  useEffect(() => {
    gl.toneMapping = NoToneMapping
  }, [gl])

  const isSatin = $table.sheen === 'Satin'
  const brightnessFactor = isSatin ? 1 : 1
  const LIGHT_INTENSITY_LEGS = 3e9 * brightnessFactor
  const LIGHT_INTENSITY_TABLETOP = 3e5 * brightnessFactor
  const MIN_DIMENSION = 40
  const SCENE_UNITS_PER_INCH = 25.4
  const halfLength = (Math.max($table.$fullLength ?? MIN_DIMENSION, MIN_DIMENSION) * SCENE_UNITS_PER_INCH) / 2
  const halfWidth = (Math.max($table.$fullWidth ?? MIN_DIMENSION, MIN_DIMENSION) * SCENE_UNITS_PER_INCH) / 2

  return (
    <>
      <SelectiveLightingRenderer />
      <NormalMapLodBiasEnforcer />
      <DistressBumpRepeatFixer />

      {/* Grey background */}
      <color attach="background" args={['#eee']} />

      {/* HDRI Environment */}
      <Environment files={environmentUrl} />

      {/* Table legs lights — only affect base objects via SCENE_BASE_LAYER */}
      <directionalLight layers={SCENE_BASE_LAYER} position={[0, 1000, 0]} intensity={1.5 * brightnessFactor} />
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[halfWidth, -500, halfLength * 1.5]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[halfWidth, -500, -halfLength * 1.5]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[-halfWidth, -500, halfLength * 1.5]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[-halfWidth, -500, -halfLength * 1.5]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />
      {/* Table legs lights (Affects benches) */}
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[halfWidth * 3, -500, 0]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />
      <pointLight
        layers={SCENE_BASE_LAYER}
        position={[-halfWidth * 3, -500, 0]}
        intensity={LIGHT_INTENSITY_LEGS}
        decay={3}
        color="#e6dfd2"
      />

      {/* Tabletop point lights — only affect tabletop objects via SCENE_TABLETOP_LAYER */}
      <pointLight
        layers={SCENE_TABLETOP_LAYER}
        position={[0, 1300, 750]}
        intensity={LIGHT_INTENSITY_TABLETOP}
        decay={2}
      />
      <pointLight
        layers={SCENE_TABLETOP_LAYER}
        position={[0, 1300, -750]}
        intensity={LIGHT_INTENSITY_TABLETOP}
        decay={2}
      />
      <pointLight
        layers={SCENE_TABLETOP_LAYER}
        position={[1000, 1300, 0]}
        intensity={LIGHT_INTENSITY_TABLETOP}
        decay={2}
      />
      <pointLight
        layers={SCENE_TABLETOP_LAYER}
        position={[-1000, 1300, 0]}
        intensity={LIGHT_INTENSITY_TABLETOP}
        decay={2}
      />
      {/* Tabletop "soft" lights — only affect tabletop objects via SCENE_TABLETOP_LAYER */}
      <directionalLight
        layers={SCENE_TABLETOP_LAYER}
        position={[1000, 1000, 1000]}
        intensity={0.6 * brightnessFactor}
      />
      <directionalLight
        layers={SCENE_TABLETOP_LAYER}
        position={[-1000, 1000, 1000]}
        intensity={0.6 * brightnessFactor}
      />
      <directionalLight
        layers={SCENE_TABLETOP_LAYER}
        position={[1000, 1000, -1000]}
        intensity={0.6 * brightnessFactor}
      />
      <directionalLight
        layers={SCENE_TABLETOP_LAYER}
        position={[-1000, 1000, -1000]}
        intensity={0.6 * brightnessFactor}
      />
      {/* a bit of light from bottom */}
      <directionalLight layers={SCENE_TABLETOP_LAYER} position={[0, -1000, 0]} intensity={1 * brightnessFactor} />
      <ContactShadows
        position={[0, 0, 0]} // Move to table center
        opacity={0.3}
        blur={1.3}
        near={0}
        far={10000}
        width={3000}
        height={3000}
        resolution={4096}
        color="#000000"
      />

      {/* Optional debugging helpers */}
      {/* <axesHelper args={[2000]} /> */}
      {/* <gridHelper args={[20000, 50, 0x0f0f0f, 0x0f0f0f]} /> */}
    </>
  )
})
